import { ColumnDef } from '@tanstack/table-core';
import { Button, IconButton, Tooltip } from '@mui/material';
import { Visibility, VisibilityOutlined } from '@mui/icons-material';

import { Visit } from '../../types/Visit';

import AuthService from '../../services/auth.service';

import { dateHeader, formatCellDate } from './timeModeCell';
import { linkTo } from '../linkTo';

const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;

export const makeVisitHistoryColumns = (
  onEyeClick?: (visitId: string) => void,
  selectedVisitId?: string | null,
  onViewDetails?: (visitId: string) => void,
): ColumnDef<Visit>[] => {
  return [
    {
      id: 'mapView',
      header: '',
      size: 50,
      cell: ({ row }) => {
        const isActive = row.original.visitId === selectedVisitId;
        return (
          <span data-row-selected={isActive ? 'true' : 'false'}>
            <Tooltip title='View on map'>
              <span>
                <IconButton
                  size='small'
                  disabled={!row.original.visitId}
                  onClick={() => row.original.visitId && onEyeClick?.(row.original.visitId)}
                  sx={{ color: isActive ? '#7c3aed' : 'inherit' }}
                >
                  {isActive ? <Visibility fontSize='small' /> : <VisibilityOutlined fontSize='small' />}
                </IconButton>
              </span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      header: 'Details',
      size: 150,
      // cell: ({ row }) => linkTo('/devices/visit-log', row.original.deviceId, 'View Details', '_blank'),
      cell: ({ row }) => (
        <Button
          size='small'
          variant='text'
          disabled={!row.original.visitId}
          onClick={() => {
            if (!row.original.visitId) return;

            onEyeClick?.(row.original.visitId);
            onViewDetails?.(row.original.visitId);
          }}
          sx={{ textTransform: 'none', color: '#7c3aed', p: 0, minWidth: 0 }}
        >
          View Details
        </Button>
      ),
    },
    {
      header: 'Serial Number',
      accessorKey: 'serialNumber',
      enableColumnFilter: true,
      size: 180,
      cell: ({ row }) => {
        const serialNumber = row.original.serialNumber;

        if (isExternalUser) {
          return serialNumber || '-';
        }

        return linkTo('/equipment', row.original.equipmentId ?? '', serialNumber);
      },
    },
    {
      header: 'Station',
      accessorKey: 'stationIataCode',
      enableColumnFilter: true,
      size: 150,
      cell: ({ row }) => row.original.stationIataCode || '-',
    },
    {
      header: 'Closest Facility',
      accessorKey: 'facility',
      size: 150,
      cell: ({ row }) => row.original.closestFacilityName || '-',
    },
    {
      header: dateHeader('First Seen'),
      accessorKey: 'firstSeen',
      size: 150,
      cell: ({ row, table }) => {
        try {
          return row.original.firstSeenDateTime ? formatCellDate(table, row.original.firstSeenDateTime) : '-';
        } catch {
          return '-';
        }
      },
    },
    {
      header: dateHeader('Last Seen'),
      accessorKey: 'lastSeen',
      size: 150,
      cell: ({ row, table }) => {
        try {
          return row.original.lastSeenDateTime ? formatCellDate(table, row.original.lastSeenDateTime) : '-';
        } catch {
          return '-';
        }
      },
    },
    {
      header: 'Total Sightings',
      accessorKey: 'totalSightings',
      size: 140,
      cell: ({ row }) => row.original.totalSightings ?? 0,
    },
  ];
};
